/**
 * Deterministic parent/sub-clause candidate discovery for the agent's flatten
 * workflow.
 *
 * The LLM cannot see Word's list-level metadata (it only receives {index, text}
 * per paragraph). That means it cannot reliably tell a "parent header" (short
 * list-item at level N whose next paragraphs are its children at level N+1)
 * apart from a "standalone clause" (list-item at level N with no children).
 *
 * This module scans the live document via Office.js, groups contiguous
 * (parent at level N, children at level N+1) runs, and returns them so the
 * agent can just consume the list and emit one `flatten_sub_clauses` op per
 * candidate — no guessing required.
 */

/* global Word */

import { log, logError } from '../../utils/logging';
import { formatOfficeJsError } from '../../utils/officeErrorFormat';

export interface FlattenCandidate {
  parent_index: number;
  parent_text: string;
  parent_level: number;
  children: Array<{ index: number; text: string; level: number }>;
}

export interface FlattenCandidatesResult {
  ok: boolean;
  paragraph_count: number;
  candidate_count: number;
  candidates: FlattenCandidate[];
  detail?: string;
}

/** Heuristic: parent header is short (few real chars) OR ends with a colon/dash. */
function looksLikeParentHeader(text: string): boolean {
  const t = (text ?? '').replace(/\s+/g, ' ').trim();
  if (!t) return false;
  // Strip a leading list-number-ish token so we judge the *content* length only.
  const withoutLeadingNumber = t.replace(
    /^[\u200e\u200f\u061c\s]*\d+(?:\.\d+)*[.)\-\s]+/u,
    '',
  );
  const content = withoutLeadingNumber.length > 0 ? withoutLeadingNumber : t;
  if (content.length <= 60) return true;
  // Long-ish parents are still valid if they clearly end with a colon or hebrew-dash.
  if (/[:\-]\s*$/.test(content)) return true;
  return false;
}

/**
 * Scan the document body and return every (parent → children) group where a
 * list-item paragraph is followed by one or more paragraphs at exactly
 * `parent_level + 1`.
 *
 * IMPORTANT — coordinate space:
 * The returned `parent_index` / children `index` values are in the
 * **agent-paragraph coordinate space** used by `agent_paragraph_index`
 * throughout the restructure ops. That space is `body.paragraphs.items`
 * filtered to non-empty text (same filter as `buildInitialAnchors` in
 * `DocumentRestructurer`). The raw `body.paragraphs.items` positions differ
 * because Word exposes empty paragraphs (blank lines between sections) that
 * the agent pipeline skips.
 */
export async function findFlattenCandidates(): Promise<FlattenCandidatesResult> {
  if (typeof Word === 'undefined' || typeof Word.run !== 'function') {
    return { ok: false, paragraph_count: 0, candidate_count: 0, candidates: [], detail: 'Word.js unavailable' };
  }

  try {
    return await Word.run(async (context) => {
      const paragraphs = context.document.body.paragraphs;
      paragraphs.load('items');
      await context.sync();

      const nRaw = paragraphs.items.length;
      const paraItems = paragraphs.items as unknown as Word.Paragraph[];
      const isListItemArrRaw: boolean[] = new Array(nRaw).fill(false);
      const levelArrRaw: number[] = new Array(nRaw).fill(-1);
      const textArrRaw: string[] = new Array(nRaw).fill('');

      const BATCH = 150;
      for (let start = 0; start < nRaw; start += BATCH) {
        const end = Math.min(start + BATCH, nRaw);
        for (let i = start; i < end; i++) {
          paraItems[i].load('text,isListItem');
        }
        await context.sync();
        for (let i = start; i < end; i++) {
          isListItemArrRaw[i] = !!paraItems[i].isListItem;
          textArrRaw[i] = paraItems[i].text ?? '';
        }
        for (let i = start; i < end; i++) {
          if (!isListItemArrRaw[i]) continue;
          paraItems[i].listItem.load('level');
        }
        await context.sync();
        for (let i = start; i < end; i++) {
          if (!isListItemArrRaw[i]) continue;
          const lvl = paraItems[i].listItem.level;
          levelArrRaw[i] = typeof lvl === 'number' ? lvl : -1;
        }
      }

      // Filter to the agent-paragraph coordinate space (skip empty-text paragraphs).
      // This MUST match buildInitialAnchors() in DocumentRestructurer.ts so that
      // parent_index / children[].index values are usable directly as
      // agent_paragraph_index in subsequent restructure ops.
      const isListItem: boolean[] = [];
      const level: number[] = [];
      const text: string[] = [];
      for (let i = 0; i < nRaw; i++) {
        if ((textArrRaw[i] || '').trim().length === 0) continue;
        isListItem.push(isListItemArrRaw[i]);
        level.push(levelArrRaw[i]);
        text.push(textArrRaw[i]);
      }
      const n = text.length;

      const candidates: FlattenCandidate[] = [];
      const used = new Set<number>();

      for (let i = 0; i < n; i++) {
        if (used.has(i)) continue;
        if (!isListItem[i]) continue;
        const parentLevel = level[i];
        if (parentLevel < 0) continue;

        const parentText = (text[i] || '').trim();
        if (!parentText) continue;
        if (!looksLikeParentHeader(parentText)) continue;

        const children: FlattenCandidate['children'] = [];
        let j = i + 1;
        while (j < n) {
          if (!isListItem[j]) break;
          const childLevel = level[j];
          if (childLevel !== parentLevel + 1) break;
          const childText = (text[j] || '').trim();
          if (!childText) {
            j += 1;
            continue;
          }
          children.push({ index: j, text: childText, level: childLevel });
          j += 1;
        }
        if (children.length === 0) continue;
        candidates.push({
          parent_index: i,
          parent_text: parentText,
          parent_level: parentLevel,
          children,
        });
        used.add(i);
        for (const c of children) used.add(c.index);
        // Do not skip past children — allow nested parents at even deeper levels
        // to be discovered independently on next outer-loop iteration.
      }

      log('[FlattenCandidates] scan', {
        raw_paragraph_count: nRaw,
        paragraph_count: n,
        candidate_count: candidates.length,
        preview: candidates.slice(0, 8).map((c) => ({
          parent_index: c.parent_index,
          parent_level: c.parent_level,
          parent_text_snippet: c.parent_text.slice(0, 60),
          children_count: c.children.length,
        })),
      });

      return {
        ok: true,
        paragraph_count: n,
        candidate_count: candidates.length,
        candidates,
      };
    });
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : String(e);
    logError('[FlattenCandidates] scan failed', { msg, office: formatOfficeJsError(e) });
    return {
      ok: false,
      paragraph_count: 0,
      candidate_count: 0,
      candidates: [],
      detail: msg,
    };
  }
}
